import React from "react";
import styled from "styled-components";
import SampleSection from "./SampleSection";
import AudioControlsContainer from "./AudioControlsContainer";
import FilterControl from "./FilterControl";
import OtherFXControls from "./OtherFXControls";
import FXDrawer from "./FXDrawer";

interface SampleAndFXSectionProps {
  currentInstrument: any;
  waveformData: number[];
  sampleDuration?: number;
  onInstrumentChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onPreviousInstrument?: () => void;
  onNextInstrument?: () => void;
  onShuffle?: () => void;
  onLock?: () => void;
  onPlay?: () => void;
  onExport?: () => void;
  mappingMode?: string | null;
  fxModes?: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  };
  onMappingModeChange?: (mode: string | null) => void;
  onFxModeChange?: (modes: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  }) => void;
  onInitializeStepsWithGlobalValue?: (
    fxType: string,
    globalValue: number
  ) => void;
  hasMappingApplied?: (fxType: string) => boolean;
  refreshTrigger?: number;
}

const SampleAndFXSection: React.FC<SampleAndFXSectionProps> = ({
  currentInstrument,
  waveformData,
  sampleDuration = 1.0,
  onInstrumentChange,
  onPreviousInstrument,
  onNextInstrument,
  onShuffle,
  onLock: _onLock,
  mappingMode,
  fxModes,
  onMappingModeChange,
  onFxModeChange,
  onInitializeStepsWithGlobalValue,
  hasMappingApplied,
  refreshTrigger,
}) => {
  return (
    <AudioControlsContainer
      mappingMode={mappingMode}
      fxModes={fxModes}
      onMappingModeChange={onMappingModeChange}
      onFxModeChange={onFxModeChange}
      onInitializeStepsWithGlobalValue={onInitializeStepsWithGlobalValue}
      hasMappingApplied={hasMappingApplied}
      refreshTrigger={refreshTrigger}
    >
      {(filterProps, otherFXProps, drawerProps) => (
        <>
          <SampleFXContainer id="sample-fx-container">
            <SampleFXContent id="sample-fx-content">
              {/* Left Section: Filter + SAMPLE */}
              <SampleSide id="sample-side">
                <FilterSampleWrapper id="filter-sample-wrapper">
                  {/* Filter Section */}
                  <FilterSectionLeft id="filter-section-left">
                    <FilterControl {...filterProps} />
                  </FilterSectionLeft>

                  {/* Sample Section */}
                  <SampleSection
                    currentInstrument={currentInstrument}
                    waveformData={waveformData}
                    sampleDuration={sampleDuration}
                    onInstrumentChange={onInstrumentChange}
                    onPreviousInstrument={onPreviousInstrument}
                    onNextInstrument={onNextInstrument}
                    onShuffle={onShuffle}
                  />
                </FilterSampleWrapper>
              </SampleSide>

              {/* Right Section: FX */}
              <FXSide id="fx-side">
                <FXContent id="fx-content">
                  <OtherFXControls {...otherFXProps} />
                </FXContent>
              </FXSide>
              <FXDrawer {...drawerProps} />
            </SampleFXContent>
          </SampleFXContainer>

          {/* FX Drawer */}
        </>
      )}
    </AudioControlsContainer>
  );
};

export default SampleAndFXSection;

// Styled Components
const SampleFXContainer = styled.div`
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  padding: 16px 16px 0px 16px;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
    margin: 10px;
    max-width: none;
  }
`;

const SampleFXContent = styled.div.attrs({
  id: "sample-fx-content-wrapper",
})`
  display: flex;
  width: 100%;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SampleSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  .sample-section {
    margin: 0;
    border-radius: 0;
    box-shadow: none;
    flex: 1;
  }

  @media (max-width: 768px) {
    border-right: none;
    border-bottom: 1px solid #d0d0d0;
  }
`;

const FilterSampleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  height: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterSectionLeft = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0px 12px 0px 0px;
  border-right: none;
  background: transparent;

  @media (max-width: 768px) {
    border-right: none;
    border-bottom: 1px solid #d0d0d0;
    padding: 15px 10px;
    min-width: auto;
  }
`;

const FXSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const FXContent = styled.div``;
